import type { InternalItem, ShelvingCellConfig, WardrobeModule } from "../types";
import {
  getShelvingCellLayouts,
  getShelvingGridLayout,
  SHELVING_PANEL_THICKNESS_MM,
  shelvingCellDividerXs,
  shelvingCellFacadeRect,
  shelvingCellShelfYs,
  type ShelvingCellLayout
} from "./shelving";
import { cellFrontLeaves, shelvingExcludedLifts } from "./flapFronts";

/**
 * Подсветка: LED-лента в алюминиевом профиле. В стеллаже её ставят в ячейку —
 * горизонтально под верхней полкой проёма или вертикально в обе стойки.
 * Цвет света выбирает покупатель: тёплый или холодный.
 */
export type LightTemperature = "warm" | "cold";
/**
 * Где стоит свет в ячейке. «horizontal» — лента сверху, под полкой.
 * «По периметру» и «точечные» убраны (решение Сергея 28.09): в старых проектах
 * периметр читается как «в стойках», точечные — как «сверху».
 */
export type ShelvingLighting = "none" | "horizontal" | "vertical";
/** Ближе к чему: у передней кромки свет падает на вещи, у задней — заливает задник. */
export type LightPosition = "front" | "back";

export const LIGHT_TEMPERATURES: Record<LightTemperature, {
  name: string;
  kelvin: number;
  /** Цвет самой ленты в профиле. */
  strip: number;
  /** Цвет света, которым она освещает ячейку. */
  light: number;
  css: string;
}> = {
  // Цвет света — излучение абсолютно чёрного тела при 3000 K и 6000 K,
  // приведённое к балансу белого комнаты (~4500 K), как на фото интерьера:
  // тёплый — мягкий золотистый, а не оранжевый; холодный — чистый белый
  // с лёгкой голубизной, а не синий.
  warm: { name: "Тёплый", kelvin: 3000, strip: 0xffe4c4, light: 0xffcf96, css: "#ffd6a3" },
  cold: { name: "Холодный", kelvin: 6000, strip: 0xf8faff, light: 0xf0f4ff, css: "#eef3ff" }
};

export const SHELVING_LIGHTING_NAMES: Record<ShelvingLighting, string> = {
  none: "Без подсветки",
  horizontal: "Сверху",
  vertical: "В стойках"
};

export const SHELVING_LIGHTING_HINTS: Record<Exclude<ShelvingLighting, "none">, string> = {
  horizontal: "Лента под полкой — самый частый вариант",
  vertical: "Ленты в обеих стойках: полка освещена по всей высоте"
};

/** Профиль подсветки (угловой, прямой, беспрофильный флекс) в цену не входит. */
export const LIGHT_PROFILE_NOTE = "Тип профиля подбирает менеджер";

export const LIGHT_POSITION_NAMES: Record<LightPosition, string> = {
  front: "У передней кромки",
  back: "У задней стенки"
};

export const LIGHT_POSITION_HINTS: Record<LightPosition, string> = {
  front: "Свет падает на то, что стоит на полке",
  back: "Свет заливает заднюю стенку — ячейка кажется глубже"
};

/** Отступ профиля от краёв проёма, мм. */
export const LED_PROFILE_INSET_MM = 10;
/** Расстояние оси профиля от передней кромки или задней стенки, мм. */
export const LED_PROFILE_EDGE_MM = 40;
/** Что идёт в смету за подсветку: метры профиля с лентой, угловые стыки, блоки питания. */
export interface LightingQuantities {
  profileM: number;
  corners: number;
  drivers: number;
}

export const NO_LIGHTING: LightingQuantities = { profileM: 0, corners: 0, drivers: 0 };

export const normalizeLightTemperature = (value: unknown): LightTemperature =>
  value === "cold" ? "cold" : "warm";

export const normalizeShelvingLighting = (value: unknown): ShelvingLighting =>
  value === "horizontal" || value === "spots" ? "horizontal"
    : value === "vertical" || value === "perimeter" ? "vertical"
      : "none";

export const normalizeLightPosition = (value: unknown): LightPosition => value === "back" ? "back" : "front";

/** Подсветка ячейки с учётом содержимого: в ящиках и диагональных секциях её нет. */
export const shelvingCellLighting = (cell: Pick<ShelvingCellConfig, "content" | "lighting"> | undefined): ShelvingLighting =>
  !cell || cell.content === "drawers" || cell.content === "diagonal"
    ? "none"
    : normalizeShelvingLighting(cell.lighting);

/** Отсек ячейки между её полками и перегородками, мм от левого нижнего угла проёма. */
export interface ShelvingLightCompartment {
  left: number;
  right: number;
  bottom: number;
  top: number;
}

/**
 * Отсеки ячейки для подсветки (10.10): делят только перегородки. Под
 * дополнительными полками своих лент нет — профиль «сверху» стоит под крышей
 * ячейки, «в стойках» идёт на всю высоту ячейки сквозь полки.
 */
export const shelvingLightCompartments = (
  cell: ShelvingCellConfig | undefined,
  width: number,
  height: number
): ShelvingLightCompartment[] => lightCompartments(cell, width, height, false);

/**
 * Куда доходит свет: непрозрачная доп. полка его перекрывает, стеклянная —
 * пропускает. Для 3D: свет каждой зоны заперт в её границах.
 */
export const shelvingLightZones = (
  cell: ShelvingCellConfig | undefined,
  width: number,
  height: number
): ShelvingLightCompartment[] => lightCompartments(cell, width, height, true);

const lightCompartments = (
  cell: ShelvingCellConfig | undefined,
  width: number,
  height: number,
  splitBySolidShelves: boolean
): ShelvingLightCompartment[] => {
  const half = SHELVING_PANEL_THICKNESS_MM / 2;
  const xs = cell ? shelvingCellDividerXs(cell, width).map(item => item.x) : [];
  const ys = cell && splitBySolidShelves
    ? shelvingCellShelfYs(cell, height).filter(item => !item.glass).map(item => item.y)
    : [];
  const columns = [0, ...xs, width].slice(0, -1).map((start, index, starts) => ({
    left: index === 0 ? 0 : start + half,
    right: index === starts.length - 1 ? width : xs[index]! - half
  }));
  const rows = [0, ...ys, height].slice(0, -1).map((start, index, starts) => ({
    bottom: index === 0 ? 0 : start + half,
    top: index === starts.length - 1 ? height : ys[index]! - half
  }));
  return rows.flatMap(row => columns.map(column => ({ ...column, ...row })))
    .filter(part => part.right - part.left > 1 && part.top - part.bottom > 1);
};

/**
 * У откидного фасада на боковинах вверху ячейки стоят механизмы (корпус
 * подъёмника или кронштейн) — профиль «в стойках» верхнего отсека
 * заканчивается ниже них (10.10).
 */
export const FLAP_MECHANISM_ZONE_MM = 150;

export const shelvingCellLightTopClearMm = (
  module: WardrobeModule,
  layout: Pick<ShelvingCellLayout, "config" | "column" | "row" | "columnSpan" | "rowSpan" | "left" | "bottom" | "width" | "height">,
  grid = getShelvingGridLayout(module)
) => {
  const config = layout.config;
  if (!config || config.content !== "door" || (config.doorOpening !== "up" && config.doorOpening !== "down")) return 0;
  const rect = shelvingCellFacadeRect(module, layout, grid);
  return cellFrontLeaves(config, rect.width, rect.height, Infinity, shelvingExcludedLifts(module)).flap ? FLAP_MECHANISM_ZONE_MM : 0;
};

/** Профили отсека: длины в мм. topClearMm — сколько занято механизмом под верхом ячейки. */
const compartmentProfiles = (lighting: ShelvingLighting, part: ShelvingLightCompartment, cellHeight: number, topClearMm: number) => {
  const across = Math.max(0, part.right - part.left - LED_PROFILE_INSET_MM * 2);
  const clear = topClearMm > 0 && part.top >= cellHeight - 0.5 ? topClearMm : 0;
  const upright = Math.max(0, part.top - part.bottom - LED_PROFILE_INSET_MM * 2 - clear);
  switch (lighting) {
    case "horizontal": return [across];
    case "vertical": return upright > 0 ? [upright, upright] : [];
    default: return [];
  }
};

/** Расход на ячейку: профиль с лентой в каждом отсеке. */
export const shelvingCellLightParts = (
  lighting: ShelvingLighting,
  width: number,
  height: number,
  compartments: ShelvingLightCompartment[] = [{ left: 0, right: width, bottom: 0, top: height }],
  topClearMm = 0
) => {
  const lengths = compartments.flatMap(part => compartmentProfiles(lighting, part, height, topClearMm));
  return {
    profileMm: lengths.reduce((sum, length) => sum + length, 0),
    profiles: lengths.length,
    lengths
  };
};

/** «2 × 356 + 280 мм»: одинаковые профили вместе. */
export const formatProfileLengths = (lengths: number[]) => {
  const groups = new Map<number, number>();
  lengths.map(Math.round).forEach(length => groups.set(length, (groups.get(length) ?? 0) + 1));
  return `${[...groups].map(([length, count]) => count > 1 ? `${count} × ${length}` : `${length}`).join(" + ")} мм`;
};

/** Длина профиля в ячейке. */
export const shelvingCellLedLengthMm = (lighting: ShelvingLighting, width: number, height: number) =>
  shelvingCellLightParts(lighting, width, height).profileMm;

export interface ShelvingLightingCell {
  id: string;
  column: number;
  row: number;
  lighting: Exclude<ShelvingLighting, "none">;
  position: LightPosition;
  temperature: LightTemperature;
  width: number;
  height: number;
  lengthMm: number;
  /** Профили по отдельности, мм. */
  profileLengths: number[];
  /** Отсеки ячейки: в каждом свой свет. */
  compartments: number;
}

/** Все подсвеченные ячейки стеллажа — для 3D, техкарты и сметы. */
export const shelvingLightingCells = (module: WardrobeModule, grid = getShelvingGridLayout(module)): ShelvingLightingCell[] =>
  module.type !== "shelving" ? [] : getShelvingCellLayouts(module, grid).flatMap(layout => {
    const lighting = shelvingCellLighting(layout.config);
    if (lighting === "none") return [];
    const compartments = shelvingLightCompartments(layout.config, layout.width, layout.height);
    const parts = shelvingCellLightParts(lighting, layout.width, layout.height, compartments,
      lighting === "vertical" ? shelvingCellLightTopClearMm(module, layout, grid) : 0);
    return [{
      id: layout.id,
      column: layout.column,
      row: layout.row,
      lighting,
      position: normalizeLightPosition(layout.config?.lightPosition),
      temperature: normalizeLightTemperature(layout.config?.lightTemperature),
      width: layout.width,
      height: layout.height,
      lengthMm: parts.profileMm,
      profileLengths: parts.lengths,
      compartments: compartments.length
    }];
  });

// ---------------------------------------------------------------------------
// Подсветка всего стеллажа

/** Цоколь светит на пол; на ножках лента под дном. Стеллажу на полу — некуда. */
export const shelvingSupportsPlinthLight = (module: Pick<WardrobeModule, "shelvingBase">) =>
  module.shelvingBase !== "floor";

/** Лента цоколя: по фасаду между боковинами, мм. */
export const shelvingPlinthLightLengthMm = (module: WardrobeModule) =>
  Math.max(0, module.width - SHELVING_PANEL_THICKNESS_MM * 2 - LED_PROFILE_INSET_MM * 2);

/** Контур на стену: по верху и обеим боковинам с тыльной стороны, мм. */
export const shelvingWallLightLengthMm = (module: WardrobeModule) =>
  Math.max(0, module.width - 60) + 2 * Math.max(0, module.height - 60);

export const shelvingModuleLights = (module: WardrobeModule) => {
  const lights: Array<{ kind: "plinth" | "wall"; temperature: LightTemperature; lengthMm: number; corners: number }> = [];
  if (module.type !== "shelving") return lights;
  if (module.shelvingPlinthLight && shelvingSupportsPlinthLight(module)) {
    lights.push({ kind: "plinth", temperature: normalizeLightTemperature(module.shelvingPlinthLight), lengthMm: shelvingPlinthLightLengthMm(module), corners: 0 });
  }
  if (module.shelvingWallLight) {
    lights.push({ kind: "wall", temperature: normalizeLightTemperature(module.shelvingWallLight), lengthMm: shelvingWallLightLengthMm(module), corners: 2 });
  }
  return lights;
};

export const shelvingHasLighting = (module: WardrobeModule) =>
  shelvingLightingCells(module).length > 0 || shelvingModuleLights(module).length > 0;

/** Подсветка стеллажа: профиль по длине, угловые стыки и один блок питания на стеллаж. */
export const shelvingLightingQuantities = (module: WardrobeModule): LightingQuantities => {
  const cells = shelvingLightingCells(module);
  const accents = shelvingModuleLights(module);
  if (!cells.length && !accents.length) return NO_LIGHTING;
  const meters = (cells.reduce((sum, cell) => sum + cell.lengthMm, 0) + accents.reduce((sum, light) => sum + light.lengthMm, 0)) / 1000;
  const corners = accents.reduce((sum, light) => sum + light.corners, 0);
  return { profileM: meters, corners, drivers: 1 };
};

const PLACE_TEXT: Record<Exclude<ShelvingLighting, "none">, string> = {
  horizontal: "сверху",
  vertical: "в стойках"
};

export const lightingDescription = (lighting: ShelvingLighting, temperature: LightTemperature, position: LightPosition = "front") =>
  lighting === "none"
    ? ""
    : `подсветка ${PLACE_TEXT[lighting]} ${position === "back" ? "у задней стенки" : "у передней кромки"}, ${LIGHT_TEMPERATURES[temperature].name.toLowerCase()} свет ${LIGHT_TEMPERATURES[temperature].kelvin} K`;

export const itemLightTemperature = (item: Pick<InternalItem, "lightTemperature">) =>
  normalizeLightTemperature(item.lightTemperature);
